import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, Teleport as _Teleport, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-78ad1ef8"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["width", "height"]
const _hoisted_2 = ["transform"]

import { overlap, Snake } from "@/utils/snake";
import { useStore } from "../store";
import Block from "@/components/Block.vue";
import BindGuide from "@/components/BindGuide.vue";
import WrapConnector from "./WrapConnector.vue";
import {
  Ref,
  ref,
  computed,
  StyleValue,
  ComputedRef,
  inject,
} from "vue";
import {
  BlockWithUUID,
  Vector2,
  WrapHeadBlock,
  WrapTailBlock,
} from "@/utils/block";
import { DeepReadonly } from "@/utils/deep-readonly";
import {
  getAbsolutePositionKey,
  getFixedPositionKey,
  willBeDeletedKey,
} from "./Program.vue";


export default /*#__PURE__*/_defineComponent({
  props: {
    modelValue: null
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit }: { emit: ({
  (e: "update:modelValue", value: DeepReadonly<Snake>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  modelValue: DeepReadonly<Snake>;
}

const {
  addSnake,
  mergeToHead,
  mergeToTail,
  clearDraggingSnake,
  snakes,
  deleteSnake,
} = useStore();




const blockWithPosition = computed<[number, BlockWithUUID][]>(() => {
  let accumulatedX = 0;
  return props.modelValue.blocks.map((block) => {
    const result: [number, BlockWithUUID] = [accumulatedX, block];
    accumulatedX += block.design.width - overlap;
    return result;
  });
});

const getFixedPosition = inject(getFixedPositionKey);
const getAbsolutePosition = inject(getAbsolutePositionKey);
const willBeDeleted = inject(willBeDeletedKey);

const isActiveDeleteZone = ref(false);
const showDeleteZone = computed(() => !props.modelValue.fromTray);

// non-reactive. updated on mousedown
let tailAnchors: { pos: Readonly<Vector2>; uuid: string }[] = [];
// non-reactive. updated on mousedown
let headAnchors: { pos: Readonly<Vector2>; uuid: string }[] = [];
const down = () => {
  window.addEventListener("pointermove", mousemove);
  window.addEventListener("pointerup", mouseup);
  // window.addEventListener("touchmove", touchmove);
  // window.addEventListener("touchend", touchend);
  tailAnchors = [];
  headAnchors = [];
  for (let snake of Object.values(snakes)) {
    if (!snake.isTailCovered) {
      tailAnchors.push({ pos: snake.anchorTail, uuid: snake.uuid });
    }
    if (!snake.isHeadCovered) {
      headAnchors.push({ pos: snake.anchorNext, uuid: snake.uuid });
    }
  }
  if (props.modelValue.isTailCovered) {
    headAnchors = [];
  }
  if (props.modelValue.isHeadCovered) {
    tailAnchors = [];
  }
};

const tailWrapInfo = computed<DeepReadonly<[Vector2, Vector2]> | null>(() => {
  const tail = props.modelValue.blocks[0];
  if (tail instanceof WrapHeadBlock) {
    if (tail.connectTo === props.modelValue.uuid) {
      return [props.modelValue.anchorNext, props.modelValue.anchorTail];
    }
    if (snakes[tail.connectTo]?.visible) {
      return [snakes[tail.connectTo].anchorNext, props.modelValue.anchorTail];
    }
  }
  return null;
});

const tailHeadInfo = computed<DeepReadonly<[Vector2, Vector2]> | null>(() => {
  const head = props.modelValue.blocks[props.modelValue.blocks.length - 1];
  if (head instanceof WrapTailBlock) {
    if (snakes[head.connectTo]?.visible) {
      return [props.modelValue.anchorNext, snakes[head.connectTo].anchorTail];
    }
  }
  return null;
});

const anchorTail = computed(() => props.modelValue.anchorTail ?? 0);
const style: ComputedRef<StyleValue> = computed(() => {
  const absolutePosition: Vector2 = [
    anchorTail.value[0],
    anchorTail.value[1] - props.modelValue.height,
  ];
  if (!getFixedPosition) {
    throw new Error("Injected getFixedPosition is undefined.");
  }
  const fixedPosition = getFixedPosition(absolutePosition);
  return {
    position: "fixed",
    top: `${fixedPosition[1]}px`,
    left: `${fixedPosition[0]}px`,
  };
});

const currentBindInfo: Ref<{
  bindGuide: [Readonly<Vector2>, Readonly<Vector2>];
  toUUID: string;
  mode: "head" | "tail";
} | null> = ref(null);
const mousemove = (event: MouseEvent) => {
  move(event.movementX, event.movementY, event.shiftKey);
};
let previousTouch: Touch | null = null;
const touchmove = (event: TouchEvent) => {
  event.preventDefault();
  if (previousTouch) {
    move(
      event.touches[0].clientX - previousTouch.clientX,
      event.touches[0].clientY - previousTouch.clientY,
      event.shiftKey
    );
  }
  previousTouch = event.touches[0];
  return false;
};
const move = (movementX: number, movementY: number, shiftKey = false) => {
  const newSnake = Snake.copy(props.modelValue);
  newSnake.anchorTail[0] += movementX;
  newSnake.anchorTail[1] += movementY;
  emit("update:modelValue", newSnake);
  let hasSet = false;
  const _head = props.modelValue.anchorNext;
  for (let tailAnchor of tailAnchors) {
    if (tailAnchor.uuid === props.modelValue.uuid) continue;
    const distance =
      (tailAnchor.pos[0] - _head[0]) ** 2 + (tailAnchor.pos[1] - _head[1]) ** 2;
    if (distance <= 50 ** 2) {
      currentBindInfo.value = {
        bindGuide: [_head, tailAnchor.pos],
        toUUID: tailAnchor.uuid,
        mode: "tail",
      };
      hasSet = true;
    }
  }
  const _tail: DeepReadonly<Vector2> = anchorTail.value;
  for (let headAnchor of headAnchors) {
    if (headAnchor.uuid === props.modelValue.uuid) continue;
    const distance =
      (headAnchor.pos[0] - _tail[0]) ** 2 + (headAnchor.pos[1] - _tail[1]) ** 2;
    if (distance <= 50 ** 2) {
      currentBindInfo.value = {
        bindGuide: [headAnchor.pos, _tail],
        toUUID: headAnchor.uuid,
        mode: "head",
      };
      hasSet = true;
    }
  }
  if (!hasSet) {
    currentBindInfo.value = null;
  }
  if (!willBeDeleted || !getFixedPosition) {
    throw new Error("Injected willBeDeleted or getFixedPosition is undefined.");
  }
  isActiveDeleteZone.value =
    !props.modelValue.fromTray && willBeDeleted(getFixedPosition(_tail));
};
const mouseup = () => {
  up();
};
const touchend = () => {
  up();
};
const up = () => {
  window.removeEventListener("pointermove", mousemove);
  window.removeEventListener("pointerup", mouseup);
  // window.removeEventListener("touchmove", touchmove);
  // window.removeEventListener("touchend", touchend);
  previousTouch = null;

  if (
    willBeDeleted &&
    getFixedPosition &&
    willBeDeleted(getFixedPosition(props.modelValue.anchorTail))
  ) {
    if (!props.modelValue.fromTray) {
      deleteSnake(props.modelValue.uuid);
    }
    clearDraggingSnake();
    return;
  }
  if (currentBindInfo.value !== null) {
    if (currentBindInfo.value.mode === "head") {
      mergeToHead(currentBindInfo.value.toUUID);
    } else {
      mergeToTail(currentBindInfo.value.toUUID);
    }
    currentBindInfo.value = null;
  } else {
    const newSnake = Snake.copy(props.modelValue);
    newSnake.fromTray = false;
    addSnake(newSnake);
    clearDraggingSnake();
  }
};

if (!getAbsolutePosition) {
  throw new Error("Injected getAbsolutePosition is undefined.");
}
if (props.modelValue.fromTray) {
  const newSnake = Snake.copy(props.modelValue);
  newSnake.anchorTail = getAbsolutePosition(props.modelValue.anchorTail);
  emit("update:modelValue", newSnake);
}
down();

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: "snake",
    style: _normalizeStyle(_unref(style))
  }, [
    (_openBlock(), _createElementBlock("svg", {
      width: props.modelValue.width,
      height: props.modelValue.height + props.modelValue.bottomAnchor
    }, [
      _createElementVNode("g", {
        transform: `translate(0, ${props.modelValue.height})`
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(blockWithPosition), ([x, block]) => {
          return (_openBlock(), _createBlock(Block, {
            key: block.uuid,
            block: block,
            "update-block": () => {},
            x: x,
            y: 0
          }, null, 8, ["block", "update-block", "x"]))
        }), 128))
      ], 8, _hoisted_2)
    ], 8, _hoisted_1)),
    (_openBlock(), _createBlock(_Teleport, { to: ".program-inner-back" }, [
      (currentBindInfo.value !== null)
        ? (_openBlock(), _createBlock(BindGuide, {
            key: 0,
            positions: currentBindInfo.value.bindGuide
          }, null, 8, ["positions"]))
        : _createCommentVNode("", true),
      (_unref(tailWrapInfo) !== null)
        ? (_openBlock(), _createBlock(WrapConnector, {
            key: 1,
            positions: _unref(tailWrapInfo)
          }, null, 8, ["positions"]))
        : _createCommentVNode("", true),
      (_unref(tailHeadInfo) !== null)
        ? (_openBlock(), _createBlock(WrapConnector, {
            key: 2,
            positions: _unref(tailHeadInfo)
          }, null, 8, ["positions"]))
        : _createCommentVNode("", true)
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: ".tray" }, [
      _createVNode(_Transition, {
        name: "delete-zone-transition",
        appear: true
      }, {
        default: _withCtx(() => [
          (_unref(showDeleteZone))
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass({ 'delete-zone': true, active: isActiveDeleteZone.value })
              }, [
                _createVNode(_component_font_awesome_icon, { icon: "trash" })
              ], 2))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]))
  ], 4))
}
}

})